/*!
 * beats.js — every time in the DSL is in beats
 * © 2026 Rob Canning — GPLv3
 *
 * dur:, tdelay:, a fade, a hold, an envelope's a: d: r:, glide:, a reverb's
 * time:, a metro's every -- in the console and in a score alike -- count
 * beats of tempo(): 60 bpm unless set, so a beat is a second. tempo(120)
 * makes all of them twice as quick.
 *
 * Each cue turns its own times into seconds with secs(), where it reads
 * them, with the tempo of that moment: a tempo() change reaches the next
 * run of a cue, not one already going. Nothing is rewritten in the parsed
 * cue -- the same one runs again and again (the playhead, buttons, the
 * network) and would be turned twice.
 *
 * Not times, and left as they are: places in a recording (audio and video
 * in: out: seg:), the score's own clock (seek, a stopwatch's countdown),
 * rates (Hz, bpm, speed:, sinOsc(...)), counts.
 */

import { gridBpm } from "./audio/beatGrid.js";

/** Seconds a beat lasts now. */
export const secPerBeat = () => 60 / (Number(gridBpm()) || 60);

// Pwhite(lo, hi) arrives as values [lo], repeats hi (the parser reads a
// trailing number as a count): there the count is the range's top
const RANGED = new Set(["Pwhite", "Pexprand"]);
const RANDOM_FNS = new Set(["rand", "rnd", "irand", "birand", "hrand", "exprand", "choose"]);
const NUMERIC = /^\s*-?(?:\d+\.?\d*|\.\d+)\s*$/;

/**
 * A time (or a list, a pattern, a rand(), a follow()) in beats -> seconds.
 * `k` is seconds per beat (secPerBeat()). What is not a time -- a word,
 * "50%" of a file, "next" -- comes back as it was.
 */
export function secs(v, k = secPerBeat()) {
  if (v == null || k === 1) return v;
  if (typeof v === "number") return v * k;
  if (typeof v === "string") {
    if (NUMERIC.test(v)) return Number(v) * k;
    // "rand(1, 4)" written as text: its numbers (not a "50%")
    if (/^\s*\w+\s*\(/.test(v) && !v.includes("%")) return v.replace(/-?(?:\d+\.?\d*|\.\d+)/g, n => String(Number(n) * k));
    return v;
  }
  if (Array.isArray(v)) return v.map(x => secs(x, k));
  if (typeof v !== "object") return v;
  if (v.type === "pattern") {
    const values = (v.values ?? v.args)?.map(x => secs(x, k));
    const out = { ...v, values };
    if (v.args) out.args = values;
    if (RANGED.has(v.name) && values?.length === 1 && Number.isFinite(Number(v.repeats))) out.repeats = Number(v.repeats) * k;
    return out;
  }
  if (v.type === "funcCall" && RANDOM_FNS.has(v.name)) return { ...v, args: (v.args || []).map(x => secs(x, k)) };
  if ((v.type === "rand" || v.type === "irand" || v.type === "birand") && (v.min != null || v.max != null)) return { ...v, min: secs(v.min, k), max: secs(v.max, k) };
  // follow(...): the range it maps the signal into (and its default)
  if (Array.isArray(v.range)) return { ...v, range: v.range.map(x => secs(x, k)), ...(v.default != null ? { default: secs(v.default, k) } : {}) };
  return v;
}

/** secs() of the keys of an object that are times (an env:{a, d, r}, a reverb:{time, pre}). */
export function secsOf(obj, keys, k = secPerBeat()) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const out = { ...obj };
  for (const key of keys) if (out[key] != null) out[key] = secs(out[key], k);
  return out;
}
